import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import {
    BarChart3,
    Calendar,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Filter,
    Loader2,
    Medal,
    TrendingDown,
    TrendingUp,
    Users,
    Zap,
    Target,
    Clock,
    DollarSign,
    ShoppingCart,
    Plus,
    Percent,
    Crown,
    UserCheck,
    Activity,
    Award,
    Sparkles
} from 'lucide-react';
import Select from 'react-select';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import DateRangeSelector from '../../Components/UI/DateRangeSelector';
import styles from './StaffAnalytics.module.css';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const StaffAnalytics = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => {
        try {
            return asCtx?.activeStore?.trim() ? JSON.parse(asCtx.activeStore) : null;
        } catch {
            return null;
        }
    })();

    /* ── State Management ── */
    const [isLoading, setIsLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [error, setError] = useState(null);

    /* ── Filter State ── */
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
        endDate: new Date().toISOString().split('T')[0] // today
    });
    const [granularity, setGranularity] = useState('daily');
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [metric, setMetric] = useState('totalSales');
    const [chartType, setChartType] = useState('line');
    const [showFilters, setShowFilters] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    /* ── Filter Options ── */
    const granularityOptions = [
        { value: 'daily', label: 'Daily', icon: Calendar, maxDays: 90 },
        { value: 'weekly', label: 'Weekly', icon: Calendar, maxDays: 365 },
        { value: 'monthly', label: 'Monthly', icon: BarChart3, maxDays: 730 },
        { value: 'quarterly', label: 'Quarterly', icon: BarChart3, maxDays: 1825 }
    ];

    const metricOptions = [
        { value: 'totalSales', label: 'Total Sales', icon: DollarSign, format: 'currency' },
        { value: 'totalBookings', label: 'Total Bookings', icon: Calendar, format: 'number' },
        { value: 'totalCartItems', label: 'Cart Items', icon: ShoppingCart, format: 'number' },
        { value: 'totalAddOns', label: 'Add-ons', icon: Plus, format: 'number' },
        { value: 'avgSalePerBooking', label: 'Avg Sale per Booking', icon: Target, format: 'currency' }
    ];

    const chartTypeOptions = [
        { value: 'line', label: 'Line Chart', icon: TrendingUp },
        { value: 'bar', label: 'Bar Chart', icon: BarChart3 }
    ];

    const getSelectStyles = () => ({
        control: (base, state) => ({
            ...base,
            height: 36,
            minHeight: 36,
            maxHeight: 36,
            borderRadius: 10,
            alignItems: 'center',
            borderColor: state.isFocused ? '#6d28d9' : '#d9e2ee',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(109, 40, 217, 0.09)' : 'none',
            '&:hover': { borderColor: state.isFocused ? '#6d28d9' : '#c3d3e6' },
        }),
        valueContainer: (base) => ({
            ...base,
            minHeight: 34,
            height: 34,
            padding: '0 10px',
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
        }),
        indicatorsContainer: (base) => ({ ...base, height: 34, alignItems: 'center' }),
        singleValue: (base) => ({ ...base, color: '#334155', fontSize: '0.8rem', margin: 0, lineHeight: '1.2' }),
        placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '0.78rem', margin: 0, lineHeight: '1.2' }),
        input: (base) => ({ ...base, margin: 0, paddingTop: 0, paddingBottom: 0 }),
        dropdownIndicator: (base) => ({ ...base, padding: 6, color: '#94a3b8' }),
        indicatorSeparator: () => ({ display: 'none' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, borderRadius: 12, overflow: 'hidden', border: '1px solid #dbe4ef' }),
        option: (base, state) => ({
            ...base,
            fontSize: '0.8rem',
            backgroundColor: state.isSelected ? '#ede9fe' : state.isFocused ? '#f5f3ff' : '#fff',
            color: state.isSelected ? '#5b21b6' : '#334155',
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#f5f3ff',
            borderRadius: 6,
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#7c3aed',
            fontSize: '0.75rem',
            fontWeight: 560,
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#a855f7',
            '&:hover': {
                backgroundColor: '#e9d5ff',
                color: '#7c3aed',
            },
        }),
    });

    /* ── API Functions ── */
    const fetchStaffList = async () => {
        try {
            const response = await fetch(BASE_URL_API + "getWorkersData", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier
                }),
            });

            const data = await response.json();
            if (data.status === "success") {
                const staff = data.response?.map(worker => ({
                    value: worker.id,
                    label: worker.name,
                    gender: worker.gender,
                    role: worker.role,
                    mobile: worker.mobile,
                    email: worker.email,
                    isAvailableForCalendar: worker.isAvailableForCalendar
                })) || [];
                setStaffList(staff);
                
                // Auto-select top 3 available staff
                if (staff.length > 0 && selectedStaff.length === 0) {
                    const availableStaff = staff.filter(s => s.isAvailableForCalendar === "1");
                    setSelectedStaff(availableStaff.slice(0, Math.min(3, availableStaff.length)));
                }
            } else {
                if(data.message === "tokenExpired") {
                    authCtx.logout();
                } else {
                    setError(data.message || 'Failed to fetch staff list');
                }
            }
        } catch (error) {
            console.error('Error fetching staff list:', error);
        }
    };

    const fetchAnalytics = async () => {
        if (!selectedStaff.length || !activeStore?.storeIdentifier) return;

        setIsLoading(true);
        setError(null);
        
        try {
            const response = await fetch(BASE_URL_API + "getStaffPerformanceComparison", {
                method: "POST",
                body: JSON.stringify({
                    token : authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    granularity,
                    staffIdentifiers: selectedStaff.map(s => s.value),
                    metric
                }),
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const data = await response.json();
            if (data.status === "success") {
                setAnalyticsData(data);
            } else if (data.message === "tokenExpired") {
                authCtx.logout();
            } else {
                setError(data.message || 'Failed to fetch analytics data');
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
            setError(error.message || 'Failed to fetch analytics data');
        } finally {
            setIsLoading(false);
        }
    };

    /* ── Effects ── */
    useEffect(() => {
        if (activeStore?.storeIdentifier) {
            fetchStaffList();
        }
    }, [activeStore?.storeIdentifier]);

    useEffect(() => {
        if (selectedStaff.length > 0 && !isDatePickerOpen) {
            fetchAnalytics();
        }
    }, [dateRange, granularity, selectedStaff, metric, isDatePickerOpen]);

    /* ── Analytics Computations ── */
    const insights = useMemo(() => {
        if (!analyticsData?.staffSeries?.length) return null;

        const staff = analyticsData.staffSeries;
        const overallRanking = analyticsData.overallRanking || [];
        
        // Performance insights
        const topPerformer = overallRanking[0];
        const improvementTrends = staff.map(s => {
            const series = s.series || [];
            if (series.length < 2) return { staff: s.servedByName, trend: 'stable', change: 0 };
            
            const recent = series.slice(-7); // Last 7 data points
            const earlier = series.slice(0, Math.min(7, series.length - 7));
            
            const recentAvg = recent.reduce((sum, d) => sum + (d[metric] || 0), 0) / recent.length;
            const earlierAvg = earlier.length > 0 
                ? earlier.reduce((sum, d) => sum + (d[metric] || 0), 0) / earlier.length 
                : 0;
            
            const change = earlierAvg > 0 ? ((recentAvg - earlierAvg) / earlierAvg) * 100 : 0;
            
            return {
                staff: s.servedByName,
                trend: change > 5 ? 'improving' : change < -5 ? 'declining' : 'stable',
                change: Math.abs(change)
            };
        });

        // Attendance insights
        const attendanceData = staff.map(s => ({
            name: s.servedByName,
            attendance: s.summary?.attendancePct || 0,
            activeBuckets: s.summary?.activeBuckets || 0,
            totalBuckets: s.summary?.totalBuckets || 1
        }));

        return {
            topPerformer,
            improvementTrends,
            attendanceData,
            totalStaff: staff.length,
            dateRange: analyticsData.meta
        };
    }, [analyticsData, metric]);

    /* ── Chart Configuration ── */
    const chartConfig = useMemo(() => {
        if (!analyticsData?.chartData) return null;

        const selectedMetric = metricOptions.find(m => m.value === metric);
        const colors = [
            'rgba(99, 102, 241, 1)',    // Indigo
            'rgba(16, 185, 129, 1)',    // Emerald
            'rgba(245, 158, 11, 1)',    // Amber
            'rgba(239, 68, 68, 1)',     // Red
            'rgba(139, 92, 246, 1)',    // Purple
            'rgba(6, 182, 212, 1)',     // Cyan
            'rgba(236, 72, 153, 1)',    // Pink
            'rgba(34, 197, 94, 1)',     // Green
        ];

        const datasets = analyticsData.chartData.datasets.map((dataset, index) => ({
            label: dataset.staffName,
            data: dataset[metric] || [],
            borderColor: colors[index % colors.length],
            backgroundColor: colors[index % colors.length].replace('1)', '0.1)'),
            borderWidth: 3,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: colors[index % colors.length],
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            fill: chartType === 'line' ? false : true,
            tension: 0.4,
            // Handle inactive periods
            pointBackgroundColor: dataset.hasActivity?.map((active, i) => 
                active ? colors[index % colors.length] : 'rgba(156, 163, 175, 0.5)'
            ),
        }));

        return {
            labels: analyticsData.chartData.shortLabels || [],
            datasets
        };
    }, [analyticsData, metric, chartType]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    font: {
                        size: 12,
                        weight: '600'
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                titleColor: '#f8fafc',
                bodyColor: '#e2e8f0',
                borderColor: 'rgba(99, 102, 241, 0.2)',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                displayColors: true,
                callbacks: {
                    title: (context) => {
                        return `${context[0].label}`;
                    },
                    label: (context) => {
                        const value = context.parsed.y;
                        const selectedMetric = metricOptions.find(m => m.value === metric);
                        const formatted = selectedMetric?.format === 'currency' 
                            ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                            : value.toLocaleString('en-IN');
                        return `${context.dataset.label}: ${formatted}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    color: 'rgba(148, 163, 184, 0.1)',
                },
                ticks: {
                    color: '#64748b',
                    font: {
                        size: 11,
                        weight: '500'
                    }
                }
            },
            y: {
                grid: {
                    color: 'rgba(148, 163, 184, 0.1)',
                },
                ticks: {
                    color: '#64748b',
                    font: {
                        size: 11,
                        weight: '500'
                    },
                    callback: (value) => {
                        const selectedMetric = metricOptions.find(m => m.value === metric);
                        if (selectedMetric?.format === 'currency') {
                            return `₹${(value / 1000).toFixed(0)}K`;
                        }
                        return value.toLocaleString('en-IN');
                    }
                }
            }
        },
        elements: {
            point: {
                hoverBorderWidth: 3
            }
        }
    };

    /* ── Utility Functions ── */
    const formatValue = (value, format) => {
        if (format === 'currency') {
            return `₹${parseFloat(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
        }
        return (value || 0).toLocaleString('en-IN');
    };

    const getDateRangeDays = () => {
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    };

    const isDateRangeValid = () => {
        const days = getDateRangeDays();
        const selectedGranularity = granularityOptions.find(g => g.value === granularity);
        return days <= selectedGranularity?.maxDays;
    };

    const closeDateModal = () => {
        setIsDatePickerOpen(false);
    };

    const changeDateHandler = (startDate, endDate) => {
        setDateRange({ startDate, endDate });
    };

    const selectedRangeLabel = `${dateRange.startDate} → ${dateRange.endDate}`;

    if (!activeStore) {
        return (
            <div className={styles.noStoreWrapper}>
                <div className={styles.noStoreCard}>
                    <Activity size={40} color="#6366f1" />
                    <h3>No Store Selected</h3>
                    <p>Please select a store to view staff performance analytics.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mainWrapper}>
            {/* Header Section */}
            <div className={styles.headerSection}>
                <div className={styles.titleArea}>
                    <div className={styles.titleIcon}>
                        <BarChart3 size={20} />
                    </div>
                    <div>
                        <h1 className={styles.pageTitle}>Staff Performance Analytics</h1>
                        <p className={styles.pageSubtitle}>
                            Advanced performance comparison and insights
                        </p>
                    </div>
                </div>

                <div className={styles.headerActions}>
                    <button 
                        className={`${styles.filterToggle} ${showFilters ? styles.filterToggleActive : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter size={14} />
                        Filters
                        <ChevronDown size={12} className={showFilters ? styles.chevronUp : ''} />
                    </button>
                </div>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
                {showFilters && (
                    <motion.div 
                        className={styles.filterPanel}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                    >
                        <div className={styles.filterGrid}>
                            {/* Date Range */}
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Date Range</label>
                                <button
                                    className={styles.dateRangeBtn}
                                    onClick={() => setIsDatePickerOpen(true)}
                                    type="button"
                                >
                                    <Calendar size={14} />
                                    <span>{selectedRangeLabel}</span>
                                </button>
                                {!isDateRangeValid() && (
                                    <p className={styles.dateError}>
                                        Date range too wide for {granularity} granularity (max {granularityOptions.find(g => g.value === granularity)?.maxDays} days)
                                    </p>
                                )}
                            </div>

                            {/* Granularity */}
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Granularity</label>
                                <div className={styles.buttonGroup}>
                                    {granularityOptions.map((option) => {
                                        const IconComponent = option.icon;
                                        return (
                                            <button
                                                key={option.value}
                                                className={`${styles.filterButton} ${granularity === option.value ? styles.filterButtonActive : ''}`}
                                                onClick={() => setGranularity(option.value)}
                                            >
                                                <IconComponent size={12} />
                                                {option.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Staff Selection */}
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Staff Members (Max 20)</label>
                                <Select
                                    isMulti
                                    value={selectedStaff}
                                    onChange={setSelectedStaff}
                                    options={staffList}
                                    placeholder="Select staff members to compare..."
                                    maxMenuHeight={200}
                                    isOptionDisabled={() => selectedStaff.length >= 20}
                                    styles={getSelectStyles()}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                />
                            </div>

                            {/* Metric & Chart Type */}
                            <div className={styles.filterRow}>
                                <div className={styles.filterGroup}>
                                    <label className={styles.filterLabel}>Metric</label>
                                    <div className={styles.buttonGroup}>
                                        {metricOptions.map((option) => {
                                            const IconComponent = option.icon;
                                            return (
                                                <button
                                                    key={option.value}
                                                    className={`${styles.filterButton} ${metric === option.value ? styles.filterButtonActive : ''}`}
                                                    onClick={() => setMetric(option.value)}
                                                >
                                                    <IconComponent size={12} />
                                                    {option.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className={styles.filterGroup}>
                                    <label className={styles.filterLabel}>Chart Type</label>
                                    <div className={styles.buttonGroup}>
                                        {chartTypeOptions.map((option) => {
                                            const IconComponent = option.icon;
                                            return (
                                                <button
                                                    key={option.value}
                                                    className={`${styles.filterButton} ${chartType === option.value ? styles.filterButtonActive : ''}`}
                                                    onClick={() => setChartType(option.value)}
                                                >
                                                    <IconComponent size={12} />
                                                    {option.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Error State */}
            {error && (
                <div className={styles.errorCard}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <div>
                        <h3>Error Loading Analytics</h3>
                        <p>{error}</p>
                        <button onClick={fetchAnalytics} className={styles.retryButton}>
                            Try Again
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingWrapper}>
                    <Loader2 size={32} className={styles.loadingSpinner} />
                    <p className={styles.loadingText}>Analyzing performance data...</p>
                </div>
            )}

            {/* Main Content */}
            {!isLoading && analyticsData && !error && (
                <div className={styles.contentGrid}>
                    {/* Insights Cards */}
                    {insights && (
                        <div className={styles.insightsGrid}>
                            {/* Top Performer Card */}
                            <motion.div 
                                className={styles.insightCard}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <div className={styles.insightHeader}>
                                    <Crown size={18} className={styles.crownIcon} />
                                    <span>Top Performer</span>
                                </div>
                                <div className={styles.insightContent}>
                                    <div className={styles.performerName}>
                                        {insights.topPerformer?.staffName || 'N/A'}
                                    </div>
                                    <div className={styles.performerValue}>
                                        {formatValue(insights.topPerformer?.metricValue, metricOptions.find(m => m.value === metric)?.format)}
                                    </div>
                                    <div className={styles.performerMeta}>
                                        {insights.topPerformer?.attendancePct?.toFixed(1)}% attendance
                                    </div>
                                </div>
                            </motion.div>

                            {/* Trends Card */}
                            <motion.div 
                                className={styles.insightCard}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                            >
                                <div className={styles.insightHeader}>
                                    <TrendingUp size={18} />
                                    <span>Performance Trends</span>
                                </div>
                                <div className={styles.trendsList}>
                                    {insights.improvementTrends?.slice(0, 3).map((trend, index) => (
                                        <div key={index} className={styles.trendItem}>
                                            <span className={styles.trendName}>{trend.staff}</span>
                                            <div className={`${styles.trendBadge} ${styles[`trend${trend.trend.charAt(0).toUpperCase() + trend.trend.slice(1)}`]}`}>
                                                {trend.trend === 'improving' && <TrendingUp size={12} />}
                                                {trend.trend === 'declining' && <TrendingDown size={12} />}
                                                {trend.trend === 'stable' && <Target size={12} />}
                                                {trend.trend}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Attendance Card */}
                            <motion.div 
                                className={styles.insightCard}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <div className={styles.insightHeader}>
                                    <UserCheck size={18} />
                                    <span>Attendance Summary</span>
                                </div>
                                <div className={styles.attendanceGrid}>
                                    {insights.attendanceData?.slice(0, 4).map((attendance, index) => (
                                        <div key={index} className={styles.attendanceItem}>
                                            <div className={styles.attendanceName}>{attendance.name}</div>
                                            <div className={styles.attendancePercent}>
                                                {attendance.attendance.toFixed(1)}%
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {/* Chart Section */}
                    <motion.div 
                        className={styles.chartCard}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className={styles.chartHeader}>
                            <div>
                                <h3 className={styles.chartTitle}>
                                    Performance Comparison
                                    <span className={styles.chartSubtitle}>
                                        {metricOptions.find(m => m.value === metric)?.label} · {granularity.charAt(0).toUpperCase() + granularity.slice(1)}
                                    </span>
                                </h3>
                            </div>
                            <div className={styles.chartMeta}>
                                {analyticsData?.meta && (
                                    <span className={styles.chartRange}>
                                        {analyticsData.meta.startDate} to {analyticsData.meta.endDate} · {analyticsData.meta.totalBuckets} {granularity} periods
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className={styles.chartContainer}>
                            {chartConfig && (
                                <>
                                    {chartType === 'line' ? (
                                        <Line data={chartConfig} options={chartOptions} />
                                    ) : (
                                        <Bar data={chartConfig} options={chartOptions} />
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>

                    {/* Leaderboard */}
                    {analyticsData?.overallRanking && (
                        <motion.div 
                            className={styles.leaderboardCard}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <div className={styles.leaderboardHeader}>
                                <h3 className={styles.leaderboardTitle}>
                                    <Medal size={18} />
                                    Overall Ranking
                                    <span className={styles.leaderboardSubtitle}>
                                        Ranked by {metricOptions.find(m => m.value === metric)?.label}
                                    </span>
                                </h3>
                            </div>

                            <div className={styles.leaderboardBody}>
                                {analyticsData.overallRanking.map((staff, index) => (
                                    <motion.div
                                        key={staff.staffIdentifier}
                                        className={`${styles.leaderboardRow} ${index < 3 ? styles.leaderboardRowTop : ''}`}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.5 + index * 0.05 }}
                                    >
                                        <div className={styles.staffRank}>
                                            {index === 0 && <Crown size={16} className={styles.goldCrown} />}
                                            {index === 1 && <Medal size={14} className={styles.silverMedal} />}
                                            {index === 2 && <Medal size={14} className={styles.bronzeMedal} />}
                                            {index > 2 && <span className={styles.rankNumber}>#{staff.rank}</span>}
                                        </div>

                                        <div className={styles.staffInfo}>
                                            <div className={styles.staffName}>{staff.staffName}</div>
                                            <div className={styles.staffMeta}>
                                                {staff.attendancePct?.toFixed(1)}% attendance · Peak: {staff.peakBucketLabel}
                                            </div>
                                        </div>

                                        <div className={styles.staffMetrics}>
                                            <div className={styles.staffValue}>
                                                {formatValue(staff.metricValue, metricOptions.find(m => m.value === metric)?.format)}
                                            </div>
                                            <div className={styles.staffBookings}>
                                                {staff.totalBookings} bookings
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </div>
            )}

            {/* Empty State */}
            {!isLoading && !analyticsData && !error && selectedStaff.length === 0 && (
                <div className={styles.emptyWrapper}>
                    <div className={styles.emptyCard}>
                        <Users size={48} color="#94a3b8" />
                        <h3>Select Staff to Analyze</h3>
                        <p>Choose staff members from the filters above to start comparing their performance.</p>
                        <button 
                            onClick={() => setShowFilters(true)}
                            className={styles.emptyAction}
                        >
                            <Filter size={16} />
                            Open Filters
                        </button>
                    </div>
                </div>
            )}
            
            <DateRangeSelector
                isOpen={isDatePickerOpen}
                closeModal={closeDateModal}
                changeDateHandler={changeDateHandler}
                initialStartDate={dateRange.startDate}
                initialEndDate={dateRange.endDate}
            />
        </div>
    );
};

export default StaffAnalytics;